<template>
  <TTabsToggle
    :header="$t('forms.settings_element_rating')"
    no-move
    :changed="wasChanged"
    data-testid="rating-settings"
  >
    <menu-content-container
      :header="$t('forms.rating_title')"
      border-full
      :padding-left-right="8"
    >
      <FontSizeColor mode="global" block-name="questionAttributes" no-border />
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.rating_captions')"
      border-full
      :padding-left-right="8"
    >
      <FontSizeColor mode="global" block-name="captionsAttributes" no-border />
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.rating_segment_appearance')"
      :padding-left-right="8"
    >
      <content-row class="!block">
        <FontSizeColor
          mode="global"
          block-name="segmentsAttributes"
          no-border
        />
      </content-row>
    </menu-content-container>
    <menu-content-container>
      <content-row class="!block">
        <color-picker
          :label="$t('forms.button_color')"
          data-testid="rating-settings-segment-bg-color"
          position="left"
          :default-color="segmentBgColor.model"
          :changed="segmentBgColor.changed"
          :reset-tooltip="$t('forms.reset_value')"
          flex
          @color-change="segmentBgColor.model = $event"
          @reset="segmentBgColor.reset"
        ></color-picker>
      </content-row>
      <content-row class="!block">
        <t-input-number
          v-model="segmentSize.model"
          data-testid="rating-settings-segment-size"
          unit=""
          :label="$t('forms.rating_segment_size')"
          :min="16"
          :max="80"
          flex
          :changed="segmentSize.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="segmentSize.reset"
        />
      </content-row>
      <content-row class="!block">
        <t-input-number
          v-model="segmentGap.model"
          data-testid="rating-settings-segment-gap"
          unit=""
          :label="$t('forms.rating_segment_gap')"
          :min="0"
          :max="20"
          flex
          :changed="segmentGap.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="segmentGap.reset"
        />
      </content-row>
      <content-row :margin-top="12">
        <t-switch
          v-model="autoWidth.model"
          :label="$t('forms.rating_segment_auto_width')"
          data-testid="rating-settings-auto-width"
          :changed="autoWidth.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="autoWidth.reset"
        ></t-switch>
      </content-row>
    </menu-content-container>

    <border-form
      :border="segmentBorder.model"
      :default-border="segmentBorder.defaultValue"
      :allow-directions="false"
      :container-border="false"
      :changed="segmentBorder.changed"
      :padding-left-right="8"
      data-testid="rating-settings-segment-border"
      @borderChanged="segmentBorder.model = $event.border"
      @reset="segmentBorder.reset"
    />

    <menu-content-container :padding-left-right="8">
      <content-row class="!block">
        <t-input-number
          v-model="segmentBorderRadius.model"
          data-testid="rating-settings-segment-border-radius"
          unit=""
          :label="$t('forms.rating_border_radius')"
          :min="0"
          :max="40"
          flex
          :changed="segmentBorderRadius.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="segmentBorderRadius.reset"
        />
      </content-row>
    </menu-content-container>

    <padding-form
      data-testid="rating-settings-margin"
      external-usage
      :external-padding="padding.model"
      :label="$t('forms.padding_label')"
      :changed="padding.changed"
      :default-value="padding.defaultValue"
      :padding-left-right="8"
      @padding-changed="padding.model = $event"
      @reset="padding.reset"
    />
  </TTabsToggle>
</template>

<script setup lang="ts">
  import paddingForm from "@/forms/paddingForm.vue";
  import borderForm from "@/forms/borderForm.vue";
  import colorPicker from "@/forms/elements/colorPicker.vue";
  import FontSizeColor from "@/forms/ratingBlock/fontSizeColorRating.vue";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import { TTabsToggle, TInputNumber, TSwitch } from "@topol/twig-ui";
  import { computed } from "vue";

  const flat = useBlockAttr({ mode: "global", block: "mj-rating" });
  const segments = useBlockAttr({
    mode: "global-group",
    block: "mj-rating",
    group: "segmentsAttributes",
  });

  const padding = flat.nullable("padding");

  const segmentBgColor = segments.str("background-color", "#ffffff");
  const segmentBorder = segments.nullable("border");
  const segmentBorderRadius = segments.px("border-radius", 8);
  const segmentSize = segments.px("size", 32);
  const segmentGap = segments.px("gap", 4);
  const autoWidth = segments.bool("autoWidth", true as any, false as any);

  const wasChanged = computed(
    () =>
      padding.changed ||
      segmentBgColor.changed ||
      segmentBorder.changed ||
      segmentBorderRadius.changed ||
      segmentSize.changed ||
      segmentGap.changed ||
      autoWidth.changed
  );
</script>
